import { skipToken, useQuery } from "@tanstack/react-query";
import {
  type GetStoriesTokenQueryResult,
  getStoriesToken,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const STORIES_TOKEN_QUERY_KEY = "STORIES_TOKEN";

export type StoriesTokenData = GetStoriesTokenQueryResult;

export const storiesTokenKeys = {
  all: [STORIES_TOKEN_QUERY_KEY],
  byIdentityUser: (activeIdentityUserPublicID?: string | null) => [
    ...storiesTokenKeys.all,
    activeIdentityUserPublicID,
  ],
};

const fetchStoriesTokenRequest = processResponseWithApiProblemDetails(() =>
  getStoriesToken(),
);

/**
 * Always hits the API (never the query cache) — the Stream token provider must get a token that
 * is fresh at call time, not one aged by the query's staleTime.
 */
export const fetchStoriesToken = (): Promise<StoriesTokenData> =>
  fetchStoriesTokenRequest();

/**
 * Stories identities are IdentityUser-keyed like chat — the query key includes the active
 * IdentityUser's PublicID so a context switch busts the cached token and the feed reconnects as
 * the new persona. Non-migrated users (no active IdentityUser) never fetch: stories are not
 * available for them. The backend also idempotently ensures the caller's story timeline follows
 * all current authors on every token issuance — no client-side setup state exists.
 */
export const useStoriesTokenQuery = (
  isAuthenticated: boolean,
  activeIdentityUserPublicID?: string | null,
) =>
  useQuery<StoriesTokenData>({
    queryKey: storiesTokenKeys.byIdentityUser(activeIdentityUserPublicID),
    queryFn:
      isAuthenticated && activeIdentityUserPublicID != null
        ? () => fetchStoriesToken()
        : skipToken,
    staleTime: MS_TIME.halfHour,
  });
